const STORAGE_KEY = 'nexo.slashCommands.recent';
const MAX_ENTRIES = 10;

type Listener = () => void;

function safeRead(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string');
  } catch {
    return [];
  }
}

function safeWrite(ids: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // localStorage may be unavailable (private mode, quota). Fail silently.
  }
}

// Keep an in-memory snapshot so useSyncExternalStore returns a stable reference
// between renders unless the data actually changes.
let snapshot: string[] = safeRead();
const listeners = new Set<Listener>();

function emit() {
  for (const listener of listeners) listener();
}

/**
 * MRU store of command ids (most recent first). Persisted to localStorage, scoped per-browser.
 *
 * Ids are language-agnostic; titles are re-resolved at render time so switching language
 * preserves history.
 */
export const recentCommandsStore = {
  /** Read the current list. Returns a stable reference between mutations. */
  getRecent(): string[] {
    return snapshot;
  },

  /** Mark `id` as most recently used. Deduplicates and trims to MAX_ENTRIES. */
  record(id: string): void {
    const next = [id, ...snapshot.filter((x) => x !== id)].slice(0, MAX_ENTRIES);
    snapshot = next;
    safeWrite(next);
    emit();
  },

  /** Remove all recorded entries. */
  clear(): void {
    if (snapshot.length === 0) return;
    snapshot = [];
    safeWrite(snapshot);
    emit();
  },

  /** Subscribe to changes. Returns an unsubscribe function. */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
